import { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";

function resolvePresentationUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
}

function fmtRevealDate(d) {
  if (!d) return "";
  try {
    return new Date(d.length <= 10 ? d + "T00:00:00" : d).toLocaleDateString("nl-NL", {
      day: "numeric", month: "long", year: "numeric"
    });
  } catch { return d; }
}

function LockedView({ member }) {
  return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="bende-member-locked">
      <NavLink to="/de-bende" className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] inline-flex items-center gap-2 hover:text-[#006A71]">
        <ArrowLeft className="w-3.5 h-3.5" /> Terug naar de bende
      </NavLink>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="mt-16"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-[#006A71]/30 mb-6">
          <Clock className="w-6 h-6 text-[#006A71]" />
        </div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Presto arriva</p>
        <h1 className="font-script text-6xl text-[#006A71] mt-3">{member.names}</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="font-serif text-xl italic text-[#4A6165] mt-6 leading-relaxed">
          Deze boodschap is nog even geheim...
        </p>
        {member.reveal_date && (
          <p className="font-serif text-lg text-[#1F3E42] mt-4">
            Kom terug op <strong className="not-italic text-[#006A71]">{fmtRevealDate(member.reveal_date)}</strong> voor een lief woord van {member.names}.
          </p>
        )}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] mt-8">
          Ci vediamo presto ♥
        </p>
      </motion.div>
    </div>
  );
}

export default function BendeMember() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const [presentationOpen, setPresentationOpen] = useState(false);

  useEffect(() => {
    api.get(`/bende/${slug}`).then((r) => setM(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const openLightbox = (photos, index) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox(null);
  const nextPhoto = (e) => {
    e?.stopPropagation();
    setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.photos.length });
  };
  const prevPhoto = (e) => {
    e?.stopPropagation();
    setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length });
  };

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="font-script text-5xl text-[#006A71]">Niet gevonden</h1>
        <NavLink to="/de-bende" className="btn-primary mt-8 inline-flex">Terug naar de bende</NavLink>
      </div>
    );
  }
  if (!m) return <div className="p-10 text-center font-script text-3xl">Laden...</div>;

  if (!m.unlocked) {
    return <LockedView member={m} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="bende-member-page">
      <NavLink to="/de-bende" className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] inline-flex items-center gap-2 hover:text-[#006A71]">
        <ArrowLeft className="w-3.5 h-3.5" /> Terug naar de bende
      </NavLink>

      <div className="text-center mt-10 mb-12">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Un messaggio da</p>
        <h1 className="font-script text-6xl sm:text-7xl text-[#006A71] mt-3">{m.names}</h1>
        {m.tagline && <p className="font-serif italic text-xl text-[#4A6165] mt-3">"{m.tagline}"</p>}
        <div className="section-divider"><span className="section-divider-dot" /></div>
      </div>

      {m.cover_photo_id && (
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="mb-12 flex justify-center"
        >
          <img
            src={photoUrl(m.cover_photo_id)}
            alt={m.names}
            className="max-w-full max-h-[600px] w-auto h-auto object-contain rounded-sm brutal-shadow-lg"
            data-testid="bende-member-cover"
          />
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#FFFFFF] border border-[#006A71]/15 rounded-sm p-8 sm:p-14 brutal-shadow-lg"
      >
        <p className="font-serif text-xl sm:text-2xl leading-[1.8] whitespace-pre-line text-[#1F3E42]">
          {m.message}
        </p>
        <div className="mt-10 pt-6 border-t border-[#1F3E42]/10 text-right">
          <p className="font-script text-3xl text-[#006A71]">— {m.names}</p>
        </div>
      </motion.div>

      {m.photo_ids && m.photo_ids.length > 0 && (
        <div className="mt-16">
          <div className="text-center mb-8">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Alcune foto</p>
            <h2 className="font-script text-4xl text-[#006A71] mt-2">Wat foto's</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {m.photo_ids.map((pid, i) => (
              <motion.button
                key={pid}
                type="button"
                initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                onClick={() => openLightbox(m.photo_ids, i)}
                className="polaroid text-left focus:outline-none group/photo"
                data-testid={`bende-photo-${i}`}
              >
                <div className="overflow-hidden">
                  <img
                    src={photoUrl(pid)} alt=""
                    className="w-full h-40 object-cover cursor-zoom-in transition-transform duration-500 group-hover/photo:scale-105"
                  />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-[#1F3E42]/85 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={closeLightbox}
          data-testid="bende-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-5 right-5 text-[#FAF6EF] hover:text-white p-2"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {lightbox.photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 sm:left-8 z-10 text-[#FAF6EF] hover:text-white p-3 bg-black/40 rounded-full"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 sm:right-8 z-10 text-[#FAF6EF] hover:text-white p-3 bg-black/40 rounded-full"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <motion.div
            key={lightbox.index}
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="polaroid max-w-4xl w-full" onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoUrl(lightbox.photos[lightbox.index])}
              alt=""
              className="w-full max-h-[75vh] object-contain"
            />
            {lightbox.photos.length > 1 && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#006A71] text-center mt-4">
                {lightbox.index + 1} / {lightbox.photos.length}
              </p>
            )}
          </motion.div>
        </div>
      )}

      {m.presentation_url && (
        <div className="mt-16 text-center">
          <button
            type="button"
            onClick={() => setPresentationOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="bende-presentation-btn"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Klik hier voor nog een extra verrassing
          </button>
        </div>
      )}

      {presentationOpen && m.presentation_url && (
        <div
          className="fixed inset-0 z-50 bg-[#1F3E42]/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          onClick={() => setPresentationOpen(false)}
          data-testid="bende-presentation-modal"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPresentationOpen(false); }}
            className="absolute top-4 right-4 text-[#FAF6EF] hover:text-white p-2 z-10"
            data-testid="presentation-close"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="bg-white rounded-sm w-full max-w-6xl h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title={`Verrassing van ${m.names}`}
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(resolvePresentationUrl(m.presentation_url))}`}
              width="100%"
              height="100%"
              frameBorder="0"
              className="block w-full h-full"
              allow="fullscreen"
            />
          </div>
        </div>
      )}
    </div>
  );
}
